import { Table, Tag, Text, Tooltip } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { EmailIdentity } from 'lib-shared-types-api';

import styles from './identityRecordsTable.module.scss';

type IdentityRecordsTableProps = {
  emailIdentity: EmailIdentity;
};

const labels = {
  dnsLabel: 'DomainKeys identified mail (DKIM)',
  mxDnsLabel: 'MX DNS record',
  spfDnsLabel: 'Custom mail from domain',
  customRedirectLabel: 'Tracking pixel',
};

const getStatusColor = (value: boolean): string => {
  return value ? colors.green[500] : colors.red[500];
};

export const IdentityRecordsTable = ({
  emailIdentity,
}: IdentityRecordsTableProps): JSX.Element => {
  const hasDkim = !!emailIdentity.dkim?.is_verified;
  const hasMailFrom = !!emailIdentity.mail_from?.is_verified;
  const hasDeliveryTracking =
    !!emailIdentity.delivery_tracking?.tracking_enabled;

  return (
    <Table framed={false} className={styles.root}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th width="20%">Name</Table.Th>
          <Table.Th width="20%">Selector</Table.Th>
          <Table.Th width="20%">Type</Table.Th>
          <Table.Th width="40%">Value</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {emailIdentity.dkim?.dns_record && (
          <Table.Tr>
            <Table.Td>
              <Tooltip content={labels.dnsLabel}>
                <Tag color={getStatusColor(hasDkim)} noTruncation>
                  DKIM
                </Tag>
              </Tooltip>
            </Table.Td>
            <Table.Td>{emailIdentity.dkim.dns_record.selector}</Table.Td>
            <Table.Td>{emailIdentity.dkim.dns_record.record_type}</Table.Td>
            <Table.Td>
              <Tooltip
                content={emailIdentity.dkim.dns_record.value}
                className={styles.tooltip}
              >
                <Text.Paragraph as="span" variant="4" subdued>
                  {emailIdentity.dkim.dns_record.value.slice(0, 50)}...
                </Text.Paragraph>
              </Tooltip>
            </Table.Td>
          </Table.Tr>
        )}
        {emailIdentity.mail_from?.mx_dns_record && (
          <Table.Tr>
            <Table.Td>
              <Tooltip content={labels.mxDnsLabel}>
                <Tag color={getStatusColor(hasMailFrom)} noTruncation>
                  Mail from
                </Tag>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              {emailIdentity.mail_from.mx_dns_record.selector}
            </Table.Td>
            <Table.Td>
              {emailIdentity.mail_from.mx_dns_record.record_type}
            </Table.Td>
            <Table.Td>{emailIdentity.mail_from.mx_dns_record.value}</Table.Td>
          </Table.Tr>
        )}
        {emailIdentity.mail_from?.spf_dns_record && (
          <Table.Tr>
            <Table.Td>
              <Tooltip content={labels.spfDnsLabel}>
                <Tag color={getStatusColor(hasMailFrom)} noTruncation>
                  Mail from
                </Tag>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              {emailIdentity.mail_from.spf_dns_record.selector}
            </Table.Td>
            <Table.Td>
              {emailIdentity.mail_from.spf_dns_record.record_type}
            </Table.Td>
            <Table.Td>{emailIdentity.mail_from.spf_dns_record.value}</Table.Td>
          </Table.Tr>
        )}
        {emailIdentity.delivery_tracking?.custom_redirect_domain && (
          <Table.Tr>
            <Table.Td>
              <Tooltip content={labels.customRedirectLabel}>
                <Tag color={getStatusColor(hasDeliveryTracking)} noTruncation>
                  Tracking pixel
                </Tag>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              {emailIdentity.delivery_tracking.custom_redirect_domain.selector}
            </Table.Td>
            <Table.Td>
              {
                emailIdentity.delivery_tracking.custom_redirect_domain
                  .record_type
              }
            </Table.Td>
            <Table.Td>
              {emailIdentity.delivery_tracking.custom_redirect_domain.value}
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
};
